import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { ApiFailure, Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { idempotencyKey } from '@/lib/idempotency'
import { initials } from '@/lib/format'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type Channels = Schema<'ChannelsResponse'>
type Channel = Schema<'ChannelOut'>
type Conversation = Schema<'ConversationOut'>
type CategoryFilter = Schema<'ReplyCategoryFilter'>
type Category = Schema<'ReplyCategory'>
type Thread = Schema<'ThreadResponse'>
type Message = Schema<'MessageOut'>

/**
 * One page of conversations, and one page of a thread.
 *
 * Stated here rather than left to the server's default so the "Older" control
 * and the page it pages through are the same size by construction. 25 is the
 * server's own default (`app/v1/inbox.DEFAULT_PAGE`); its ceiling is 100.
 */
const PAGE_SIZE = 25

/**
 * `outreach#inbox`, `#inbox-one` and `#inbox-zero` — one screen, three states.
 *
 * S-D4 drew them as three screens and they are one component, because they are
 * one state machine and the transition between them is a fact the server
 * reports rather than a route: **how many channels are connected.** Zero
 * replaces the inbox with the setup list, one hides the per-message channel
 * labels because they would be noise, and two or more shows them. Routing
 * between three pages would mean a tenant who connects their first channel has
 * to find a different URL for the inbox they were already looking at.
 *
 * **One thread per person, with the channel as a message label.** That is the
 * whole model and the server does the merging (`app/db/inbox.py`) — AugStash
 * has one thread per contact *per channel*, so a lead who replies on WhatsApp
 * after an email arrives here as two provider threads and one conversation.
 * Nothing in this file knows that; it renders what it is given, which is the
 * point of doing the merge on the server.
 *
 * **Replying is a permission this screen may not hold.** `inbox:read` is every
 * role's and `inbox:reply` stops at admin, because a reply is an outbound
 * message to somebody outside the organisation in its name (§8). A member sees
 * the conversation and no composer — not a composer that fails on submit.
 *
 * **The channel labels are not decoration.** A message that went out on SMS and
 * one that went out on WhatsApp cost different money and reach different
 * people, and the tenant is the one who has to know which. So the label follows
 * the message, and it disappears only when there is nothing to distinguish.
 */
export function InboxPage(): JSX.Element {
  const { role, tenant } = useActiveTenant()
  const toast = useToast()
  const replies = may(role, 'inbox:reply')
  // G18 and G19. Setting a thread's category and moving a lead are both
  // `outreach:manage`, which stops at admin — the permission's own docstring
  // argues why a decision the ladder acts on is not a note on a message.
  const manages = may(role, 'outreach:manage')

  const [search, setSearch] = useState('')
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [category, setCategory] = useState<CategoryFilter | ''>('')
  const [selected, setSelected] = useState<string | null>(null)

  // WEB-1. The endpoint has always been cursor-paginated and this screen passed
  // no cursor and never read `next_cursor`, so a tenant with more than 25
  // conversations could reach the rest only if the search box happened to match
  // them. A stack rather than a single cursor because a one-way "Older" with no
  // way back is a trap: `Leads.tsx` pages the same way, and popping the stack is
  // the only correct "Newer" when the cursor is opaque and keyset-based.
  const [cursors, setCursors] = useState<readonly (string | null)[]>([null])
  const cursor = cursors[cursors.length - 1] ?? null

  // Every filter change starts a new run. A cursor is a position in one
  // ordering, so carrying it across a filter change asks the server to continue
  // from a row that may not be in the new result set at all.
  const restart = useCallback(() => {
    setCursors([null])
  }, [])

  const channels = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/inbox/channels', { signal })),
    [],
  )
  // `search` and `unreadOnly` are in the dependency list, so typing re-runs the
  // query. Deliberately not debounced here: the query hook aborts the previous
  // request on every change (`useQuery`), so a fast typist produces one
  // in-flight request rather than a queue of them.
  const list = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/outreach/conversations', {
          // `null` rather than `undefined` for an empty search: the generated
          // types are checked with `exactOptionalPropertyTypes`, so `undefined`
          // is not assignable to `string | null` — and the server reads both as
          // "no search" (`search or None` at the boundary).
          params: {
            query: {
              search: search || null,
              unread: unreadOnly,
              category: category || null,
              limit: PAGE_SIZE,
              cursor,
            },
          },
          signal,
        }),
      ),
    [search, unreadOnly, category, cursor],
  )

  // Memoised, and not for speed: the array is the dependency of the effect
  // below, so a fresh `[]` on every render would re-run the "open the first
  // conversation" effect for ever.
  const conversations: Conversation[] = useMemo(
    () => (list.state.status === 'ready' ? list.state.data.conversations : []),
    [list.state],
  )

  // The first conversation is opened for you. A two-pane inbox whose right pane
  // is empty on arrival makes the tenant click before they can read anything,
  // and there is exactly one sensible first choice: the newest.
  //
  // It also has to *follow* the list, which it did not before WEB-1 gave the
  // list more than one page: paging to older conversations, or filtering the
  // selected one away, otherwise leaves the right pane showing a thread that is
  // no longer on the left. Guarded on `ready` because `conversations` is `[]`
  // while a refetch is in flight, and clearing the selection on every keystroke
  // of the search box would close the thread the tenant is reading.
  const ready = list.state.status === 'ready'
  useEffect(() => {
    if (!ready) return
    const first = conversations[0]
    if (!first) {
      setSelected(null)
      return
    }
    if (selected === null || !conversations.some((row) => row.id === selected)) {
      setSelected(first.id)
    }
  }, [ready, conversations, selected])

  if (channels.state.status === 'loading') return <Loading label="Loading your inbox" lines={4} />
  if (channels.state.status === 'error') {
    return <ErrorState failure={channels.state.failure} onRetry={channels.refetch} />
  }

  const connected: Channels = channels.state.data

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row" style={{ justifyContent: 'space-between' }}>
        <h1 className="ar-h1">Inbox</h1>
        {connected.connected.length > 0 ? (
          <SyncButton
            onDone={() => {
              list.refetch()
            }}
          />
        ) : null}
      </div>

      {connected.connected.length === 0 ? (
        <SetupList channels={connected.channels} />
      ) : (
        <Inbox
          conversations={conversations}
          connected={connected.connected}
          loading={list.state.status === 'loading'}
          failure={list.state.status === 'error' ? list.state.failure : null}
          onRetry={list.refetch}
          search={search}
          onSearch={(value) => {
            setSearch(value)
            restart()
          }}
          unreadOnly={unreadOnly}
          onUnreadOnly={(value) => {
            setUnreadOnly(value)
            restart()
          }}
          category={category}
          onCategory={(value) => {
            setCategory(value)
            restart()
          }}
          hasNewer={cursors.length > 1}
          hasOlder={list.state.status === 'ready' && (list.state.data.next_cursor ?? null) !== null}
          onNewer={() => {
            setCursors((stack) => (stack.length > 1 ? stack.slice(0, -1) : stack))
          }}
          onOlder={() => {
            if (list.state.status !== 'ready') return
            const next: string | null = list.state.data.next_cursor ?? null
            if (next === null) return
            setCursors((stack) => [...stack, next])
          }}
          canManage={manages}
          selected={selected}
          onSelect={setSelected}
          canReply={replies}
          tenantId={tenant.id}
          onChanged={() => {
            list.refetch()
          }}
          onSent={() => {
            toast.show({ kind: 'success', title: 'Reply sent' })
          }}
        />
      )}
    </div>
  )
}

/**
 * The zero state (S-D4 `#inbox-zero`) — a setup list, not an apology.
 *
 * Every channel §5 names, with what it unlocks and how long it takes, in the
 * order that gets somebody sending soonest. The copy is the server's
 * (`app/outreach/channels.py`) so a channel that becomes available in a later
 * session appears here with no change to this file — the same reason the usage
 * screen's meter labels come from the server.
 */
function SetupList({ channels }: { channels: Channel[] }): JSX.Element {
  return (
    <div style={{ maxWidth: '44rem', margin: '0 auto', width: '100%' }}>
      <section className="ar-card">
        <div className="ar-card__body">
          <Empty
            icon="✉"
            title="No channels connected yet"
            body="Your inbox fills up once leads can reply to you. Connect at least one — email is the fastest to set up."
          />
          <div className="ar-stack ar-stack--tight" style={{ marginTop: 'var(--ar-space-4)' }}>
            {channels.map((channel) => (
              <div className="app-conn" key={channel.channel}>
                <span className={`app-chan app-chan--${channel.channel}`} aria-hidden="true">
                  {channel.channel.slice(0, 2)}
                </span>
                <span className="app-conn__body">
                  <strong>{channel.label}</strong>
                  <span>{channel.blurb}</span>
                  {channel.reason ? <span>{channel.reason}</span> : null}
                </span>
                {/* A channel that can never carry a message gets no button. The
                    sentence above already says why, and a *Connect* control that
                    leads nowhere is worse than none. */}
                {channel.sendable ? (
                  <Link className="ar-btn ar-btn--ghost ar-btn--sm" to={channel.setup_path}>
                    {channel.action}
                  </Link>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

function SyncButton({ onDone }: { onDone: () => void }): JSX.Element {
  const sync = useMutation(() => call(() => api.POST('/api/v1/outreach/inbox/sync', {})))
  return (
    <div className="ar-row">
      <InlineError failure={sync.state.failure} />
      <Button
        tone="ghost"
        size="sm"
        busy={sync.state.status === 'running'}
        onClick={() => {
          void sync.run(undefined).then((result) => {
            if (result.ok) onDone()
          })
        }}
      >
        Refresh
      </Button>
    </div>
  )
}

interface InboxProps {
  conversations: Conversation[]
  connected: string[]
  loading: boolean
  failure: ApiFailure | null
  onRetry: () => void
  search: string
  onSearch: (value: string) => void
  unreadOnly: boolean
  onUnreadOnly: (value: boolean) => void
  category: CategoryFilter | ''
  onCategory: (value: CategoryFilter | '') => void
  /** WEB-1 — whether a newer or older page exists, and how to reach it. */
  hasNewer: boolean
  hasOlder: boolean
  onNewer: () => void
  onOlder: () => void
  canManage: boolean
  selected: string | null
  onSelect: (id: string) => void
  canReply: boolean
  tenantId: string
  onChanged: () => void
  onSent: () => void
}

function Inbox(props: InboxProps): JSX.Element {
  const { conversations, connected, selected } = props
  // One connected channel means the labels carry no information, so they go —
  // S-D4's `#inbox-one`, and the only difference between it and `#inbox`.
  const showLabels = connected.length > 1

  return (
    <div className={selected ? 'app-inbox app-inbox--reading' : 'app-inbox'}>
      <div className="app-inbox__list">
        <div className="app-inbox__filters">
          <input
            className="ar-input"
            type="search"
            placeholder="Search conversations"
            aria-label="Search conversations"
            style={{ height: 'var(--ar-control-sm)' }}
            value={props.search}
            onChange={(event) => {
              props.onSearch(event.target.value)
            }}
          />
          <select
            className="ar-select app-inbox__category"
            aria-label="Filter by reply category"
            value={props.category}
            onChange={(event) => {
              props.onCategory(event.target.value as CategoryFilter | '')
            }}
          >
            <option value="">All replies</option>
            {CATEGORY_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <label className="ar-row" style={{ gap: 'var(--ar-space-2)' }}>
            <input
              type="checkbox"
              checked={props.unreadOnly}
              onChange={(event) => {
                props.onUnreadOnly(event.target.checked)
              }}
            />
            <span style={{ fontSize: 'var(--ar-text-caption-size)' }}>Unread only</span>
          </label>
        </div>

        {props.failure ? (
          <div style={{ padding: 'var(--ar-space-4)' }}>
            <ErrorState failure={props.failure} onRetry={props.onRetry} />
          </div>
        ) : props.loading ? (
          <div style={{ padding: 'var(--ar-space-4)' }}>
            <Loading label="Loading conversations" lines={3} />
          </div>
        ) : conversations.length === 0 ? (
          <div style={{ padding: 'var(--ar-space-4)' }}>
            <Empty
              title={props.search || props.category ? 'Nothing matches' : 'No conversations yet'}
              body={
                props.search || props.category
                  ? 'Try a different name, company or phrase — or clear the category filter.'
                  : 'Replies from your leads land here. Press Refresh once a campaign has gone out.'
              }
            />
          </div>
        ) : (
          conversations.map((conversation) => (
            <button
              type="button"
              key={conversation.id}
              className="app-thread"
              aria-current={conversation.id === selected ? 'true' : undefined}
              onClick={() => {
                props.onSelect(conversation.id)
              }}
            >
              {/* The name owns its own line, with the channel and the time on a second.
                  Sharing one line meant the chip and the timestamp took their width
                  first and the name truncated into whatever was left — at 20rem that
                  cut "Priya Sharma" to "Priya …", and the sender is the one thing this
                  list exists to be scanned by. Now only a genuinely long name is
                  ellipsised, and the meta is always visible rather than pushed out
                  through the pane's clipped edge. */}
              <span className="app-thread__top">
                <strong>{conversation.full_name ?? conversation.contact_value}</strong>
                <span className="app-thread__meta">
                  {showLabels && conversation.last_channel ? (
                    <ChannelChip channel={conversation.last_channel} />
                  ) : null}
                  <span className="app-thread__time">{when(conversation.last_message_at)}</span>
                </span>
              </span>
              <span className="app-thread__snip">{conversation.snippet ?? '—'}</span>
              <span className="app-thread__tags">
                <CategoryBadge category={conversation.reply_category ?? null} />
                {conversation.unread_count > 0 ? (
                  <span className="ar-badge ar-badge--info">
                    {conversation.unread_count} unread
                  </span>
                ) : null}
              </span>
            </button>
          ))
        )}

        {/* WEB-1. Rendered only when there is somewhere to go, so a tenant with
            one page of conversations never sees a pair of dead controls. */}
        {props.hasNewer || props.hasOlder ? (
          <div
            className="ar-row"
            style={{
              justifyContent: 'space-between',
              gap: 'var(--ar-space-2)',
              padding: 'var(--ar-space-3) var(--ar-space-4)',
            }}
          >
            <Button size="sm" disabled={!props.hasNewer} onClick={props.onNewer}>
              Newer
            </Button>
            <Button size="sm" disabled={!props.hasOlder} onClick={props.onOlder}>
              Older
            </Button>
          </div>
        ) : null}
      </div>

      <div className="app-conv app-inbox__conv">
        {selected ? (
          // Keyed, so a different conversation is a different component rather
          // than the same one with new props. An effect that cleared the loaded
          // history on `conversationId` change looked equivalent and was not: a
          // page still in flight when the reader moves on resolves afterwards
          // and prepends one person's history above another's. Unmounting is
          // the only version of this that a late response cannot outrace.
          <ThreadPane
            key={selected}
            conversationId={selected}
            connected={connected}
            showLabels={showLabels}
            canReply={props.canReply}
            canManage={props.canManage}
            tenantId={props.tenantId}
            onChanged={props.onChanged}
            onSent={props.onSent}
          />
        ) : (
          <div style={{ padding: 'var(--ar-space-5)' }}>
            <Empty
              title="Nothing selected"
              body="Pick a conversation on the left to read it and reply."
            />
          </div>
        )}
      </div>
    </div>
  )
}

function ThreadPane({
  conversationId,
  connected,
  showLabels,
  canReply,
  canManage,
  tenantId,
  onChanged,
  onSent,
}: {
  conversationId: string
  connected: string[]
  showLabels: boolean
  canReply: boolean
  canManage: boolean
  tenantId: string
  onChanged: () => void
  onSent: () => void
}): JSX.Element {
  const toast = useToast()
  const thread = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/outreach/conversations/{conversation_id}', {
          params: { path: { conversation_id: conversationId } },
          signal,
        }),
      ),
    [conversationId],
  )

  const markRead = useMutation((id: string) =>
    call(() =>
      api.POST('/api/v1/outreach/conversations/{conversation_id}/read', {
        params: { path: { conversation_id: id } },
      }),
    ),
  )

  // WEB-2. The thread endpoint is paginated too, and this pane read one page and
  // never `next_cursor`, so a conversation longer than 25 messages showed its
  // newest 25 and no way to see the rest — silently, because the page it did
  // show looked complete.
  //
  // `useMutation` for a `GET`, deliberately: this is a fetch the person asks
  // for by pressing a button, so what it needs is `run`/`pending`/`failure` on
  // demand — which is the mutation hook — rather than `useQuery`'s "run on
  // mount and whenever the deps change".
  const [earlier, setEarlier] = useState<readonly Message[]>([])
  //: The cursor for the next *older* page. `undefined` means "not taken over
  //: yet" — the thread query's own `next_cursor` is still the one to use.
  const [olderCursor, setOlderCursor] = useState<string | null | undefined>(undefined)

  const loadEarlier = useMutation((from: string) =>
    call(() =>
      api.GET('/api/v1/outreach/conversations/{conversation_id}', {
        params: {
          path: { conversation_id: conversationId },
          query: { limit: PAGE_SIZE, cursor: from },
        },
      }),
    ),
  )

  // A different conversation is a different history — enforced by the `key` on
  // this component rather than by an effect here, for the reason given there.
  const resetHistory = useCallback(() => {
    setEarlier([])
    setOlderCursor(undefined)
  }, [])

  const data: Thread | null = thread.state.status === 'ready' ? thread.state.data : null
  const unread = data?.conversation.unread_count ?? 0
  const run = markRead.run

  // Opening a thread is reading it. Fired once per conversation that actually
  // has something unread — not on every render, and not for a thread that is
  // already clear, because a `POST` per keystroke of the search box would be a
  // write on a read.
  //
  // WEB-11: a refused mark had no failure path at all. The badge and the list
  // count then stay stale and the effect will not retry, because `unread` comes
  // from the thread response and does not change. A quiet warning rather than a
  // danger toast — nothing the tenant did failed, and the thread they opened is
  // on screen and readable.
  useEffect(() => {
    if (unread > 0) {
      void run(conversationId).then((result) => {
        if (result.ok) {
          onChanged()
        } else {
          toast.show({
            kind: 'warning',
            title: 'Could not mark this as read',
            body: result.failure.message,
          })
        }
      })
    }
    // `run`, `onChanged` and `toast` are stable for the life of the mutation
    // hook, the page and the provider; including them would re-fire the mark on
    // every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, unread])

  if (thread.state.status === 'loading') return <Loading label="Loading conversation" lines={4} />
  if (thread.state.status === 'error') {
    return <ErrorState failure={thread.state.failure} onRetry={thread.refetch} />
  }
  if (!data) return <Loading label="Loading conversation" lines={4} />

  const person = data.conversation.full_name ?? data.conversation.contact_value

  // Oldest-first on screen. Each page is already oldest-first within itself
  // (`ThreadResponse` reverses before it goes out) and every page fetched by the
  // cursor is older than everything already here, so an older page goes in
  // front — which is also why the control sits at the top of the list rather
  // than the bottom.
  const messages: readonly Message[] = [...earlier, ...data.messages]
  const nextCursor = olderCursor === undefined ? (data.next_cursor ?? null) : olderCursor

  return (
    <>
      <div className="app-conv__head">
        <span className="ar-avatar ar-avatar--sm" aria-hidden="true">
          {initials(person)}
        </span>
        <div>
          <strong style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>{person}</strong>
          <div className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
            {[data.conversation.company, data.conversation.headline].filter(Boolean).join(' · ') ||
              data.conversation.contact_value}
          </div>
        </div>
        <span className="ar-card__spacer" />
        {canManage ? (
          <CategorySelect
            conversationId={conversationId}
            category={data.conversation.reply_category ?? null}
            source={data.conversation.reply_category_source ?? null}
            onChanged={() => {
              thread.refetch()
              onChanged()
            }}
          />
        ) : (
          <CategoryBadge category={data.conversation.reply_category ?? null} />
        )}
        {canManage ? (
          <MoveToWon
            leadId={data.conversation.lead_id}
            stage={data.conversation.stage}
            onMoved={() => {
              thread.refetch()
              onChanged()
            }}
          />
        ) : (
          <span className="ar-badge ar-badge--neutral">{data.conversation.stage}</span>
        )}
      </div>

      <div className="app-conv__body">
        {nextCursor === null ? null : (
          <div
            className="ar-row"
            style={{ justifyContent: 'center', padding: 'var(--ar-space-2)' }}
          >
            <Button
              size="sm"
              busy={loadEarlier.pending}
              busyLabel="Loading"
              onClick={() => {
                void loadEarlier.run(nextCursor).then((result) => {
                  if (!result.ok) return
                  setEarlier((rows) => [...result.data.messages, ...rows])
                  setOlderCursor(result.data.next_cursor ?? null)
                })
              }}
            >
              Load earlier messages
            </Button>
          </div>
        )}
        <InlineError failure={loadEarlier.state.failure} />
        {messages.length === 0 ? (
          <Empty
            title="Nothing here yet"
            body="This conversation has no messages. Press Refresh to pull the latest from your channels."
          />
        ) : (
          messages.map((message) => (
            <div key={message.id} className={`app-msg app-msg--${message.direction}`}>
              {message.body}
              <div className="app-msg__meta">
                {showLabels ? <ChannelChip channel={message.channel} /> : null}
                <span>{when(message.occurred_at)}</span>
                {message.delivery_state ? (
                  <span>· {stateLabel(message.delivery_state)}</span>
                ) : null}
              </div>
            </div>
          ))
        )}
      </div>

      {canReply ? (
        <Composer
          conversationId={conversationId}
          connected={connected}
          preferred={data.conversation.last_channel}
          tenantId={tenantId}
          onSent={() => {
            // A sent message shifts the newest page by one, which pushes its
            // oldest message out of that page and into no list at all —
            // `earlier` holds only what was older than the page as it stood.
            // Dropping the loaded history is visible: the control comes back
            // and the reader presses it again. A hole in the middle of a
            // conversation is not.
            resetHistory()
            thread.refetch()
            onChanged()
            onSent()
          }}
        />
      ) : (
        <div className="app-conv__foot">
          <p className="ar-muted" style={{ margin: 0 }}>
            Replying needs an owner or admin — a reply goes to this person in your
            organisation&apos;s name.
          </p>
        </div>
      )}
    </>
  )
}

/* --- reply categories (S-B20, audit rows G17-G19) -------------------------- */

/**
 * The filter's options, as `outreach.html#inbox` draws them.
 *
 * **Five options over four stored values, and the mismatch is deliberate.**
 * *All replies* is the parameter being absent, and *Not classified* selects
 * `NULL` **or** `neutral` — a thread nobody has looked at and one the model read
 * and found nothing actionable in. `app/db/models/tenant.REPLY_CATEGORIES`
 * argues the union: they differ only in whether a model looked, which is a fact
 * for support and not a difference in what the tenant does next.
 */
const CATEGORY_FILTERS: readonly { value: CategoryFilter; label: string }[] = [
  { value: 'interested', label: 'Interested' },
  { value: 'question', label: 'Question' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'unclassified', label: 'Not classified' },
]

/** What a person may set a thread to. `neutral` is here and is not in the filter:
 *  a tenant reading an out-of-office should be able to say so rather than being
 *  pushed into one of the three that imply an intention. */
const CATEGORY_CHOICES: readonly { value: Category; label: string }[] = [
  { value: 'interested', label: 'Interested' },
  { value: 'question', label: 'Question' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'neutral', label: 'Nothing to act on' },
]

const CATEGORY_TONES: Record<string, string> = {
  interested: 'success',
  question: 'info',
  not_interested: 'warning',
  neutral: 'neutral',
}

const CATEGORY_LABELS: Record<string, string> = {
  interested: 'Interested',
  question: 'Question',
  not_interested: 'Not interested',
  neutral: 'Not classified',
}

/**
 * The label on a thread row.
 *
 * `neutral` renders as **Not classified**, which is the prototype's own reading:
 * it files *"Thanks for connecting."* under that badge, and that is exactly what
 * the classifier calls `neutral`. A tenant has nothing to do about either, and a
 * sixth badge for the difference would be a distinction drawn for the database's
 * benefit rather than the reader's.
 */
function CategoryBadge({ category }: { category: string | null }): JSX.Element {
  if (category === null) return <span className="ar-badge ar-badge--neutral">Not classified</span>
  return (
    <span className={`ar-badge ar-badge--${CATEGORY_TONES[category] ?? 'neutral'}`}>
      {CATEGORY_LABELS[category] ?? category}
    </span>
  )
}

/**
 * G18 — the select that overrules the classifier.
 *
 * §5 requires the category be *"a label you can overrule, never a silent
 * decision that files the thread away"*. Setting it writes
 * `reply_category_source = 'tenant'`, and no later classification touches the
 * thread again — which is what makes overruling it mean something rather than
 * being undone by the next sweep. The screen says which of the two decided it,
 * because *"Haiku thinks"* and *"you said"* are different claims.
 */
function CategorySelect({
  conversationId,
  category,
  source,
  onChanged,
}: {
  conversationId: string
  category: string | null
  source: string | null
  onChanged: () => void
}): JSX.Element {
  const toast = useToast()
  const set = useMutation((next: Category | null) =>
    call(() =>
      api.PUT('/api/v1/outreach/conversations/{conversation_id}/category', {
        params: { path: { conversation_id: conversationId } },
        body: { category: next },
      }),
    ),
  )
  return (
    <span className="app-conv__category">
      <select
        className="ar-select app-conv__categorySelect"
        aria-label="Reply category"
        disabled={set.pending}
        value={category ?? ''}
        onChange={(event) => {
          const value = event.target.value
          void set.run(value === '' ? null : (value as Category)).then((result) => {
            if (result.ok) {
              onChanged()
            } else {
              // WEB-5. The `<select>` is controlled, so a refused change snaps
              // back to the `category` prop on the next render — and did so
              // with no message at all, which reads as the control ignoring the
              // press. A toast rather than an inline block because it has to
              // appear at the moment of the snap-back and this select sits in a
              // flex header a form-summary would break.
              toast.show({
                kind: 'danger',
                title: 'Could not change the category',
                body: result.failure.message,
              })
            }
          })
        }}
      >
        <option value="">Not classified</option>
        {CATEGORY_CHOICES.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span className="ar-subtle app-conv__categoryBy">
        {category === null ? '' : source === 'tenant' ? 'set by you' : 'by Haiku 4.5'}
      </span>
    </span>
  )
}

/**
 * G19 — *Move to Won*, from the conversation that justifies it.
 *
 * On the thread rather than only on the lead, because this is where a tenant is
 * when they decide: the message saying *"send us the contract"* is on screen.
 * Moving to `won` also ends that person's follow-up ladder, which the toast says
 * out loud — a control with a second effect nobody mentioned is one a tenant
 * learns about from a prospect's complaint.
 */
function MoveToWon({
  leadId,
  stage,
  onMoved,
}: {
  leadId: string
  stage: string
  onMoved: () => void
}): JSX.Element {
  const toast = useToast()
  const move = useMutation(() =>
    call(() =>
      api.POST('/api/v1/outreach/leads/{lead_id}/stage', {
        params: { path: { lead_id: leadId } },
        body: { stage: 'won' },
      }),
    ),
  )
  if (stage === 'won') return <span className="ar-badge ar-badge--brand">Won</span>
  return (
    <Button
      size="sm"
      busy={move.pending}
      onClick={() => {
        void move.run(undefined).then((result) => {
          if (result.ok) {
            toast.show({
              kind: 'success',
              title: 'Moved to Won',
              body:
                result.data.follow_ups_cancelled > 0
                  ? 'Their follow-ups have stopped.'
                  : 'Nothing was scheduled for them.',
            })
            onMoved()
          } else {
            // WEB-6. A refused move left the button idle and said nothing, so
            // the stage silently stayed where it was. `LeadDetail.tsx:102-110`
            // has always done this correctly for the same endpoint.
            toast.show({
              kind: 'danger',
              title: 'Could not move to Won',
              body: result.failure.message,
            })
          }
        })
      }}
    >
      Move to Won
    </Button>
  )
}

/**
 * The reply box: a channel to send on, the words, and Send.
 *
 * **The channel is chosen, never inferred.** It defaults to the one the last
 * message arrived on, because that is where the conversation is — but it is a
 * control rather than a rule, since a lead who wrote from a phone may want the
 * rate card by email. Only connected channels are offered, so a channel that
 * would be refused by the server is not offered by the screen.
 */
function Composer({
  conversationId,
  connected,
  preferred,
  tenantId,
  onSent,
}: {
  conversationId: string
  connected: string[]
  preferred: string | null | undefined
  tenantId: string
  onSent: () => void
}): JSX.Element {
  // `?? ''` is unreachable: the composer is only rendered when at least one
  // channel is connected. It is there because the type cannot say so, and an
  // assertion that lies is worse than a fallback that never fires.
  const initial = useMemo(
    () => (preferred && connected.includes(preferred) ? preferred : (connected[0] ?? '')),
    [preferred, connected],
  )
  const [channel, setChannel] = useState(initial)
  const [body, setBody] = useState('')

  // The default follows the conversation: switching threads must not leave the
  // composer pointed at the channel the *previous* thread was on.
  useEffect(() => {
    setChannel(initial)
    setBody('')
  }, [initial, conversationId])

  const send = useMutation((input: { channel: string; body: string }) =>
    call(() =>
      api.POST('/api/v1/outreach/replies', {
        body: { conversation_id: conversationId, channel: input.channel, body: input.body },
        params: {
          header: { 'Idempotency-Key': idempotencyKey('reply', `${tenantId}:${conversationId}`) },
        },
      }),
    ),
  )

  const onSend = useCallback(async (): Promise<void> => {
    if (!body.trim()) return
    const result = await send.run({ channel, body })
    if (!result.ok) return
    setBody('')
    onSent()
  }, [body, channel, send, onSent])

  return (
    <div className="app-conv__foot">
      <div className="ar-row">
        <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
          Reply via
        </span>
        <div className="app-seg" role="group" aria-label="Reply channel">
          {connected.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={option === channel}
              onClick={() => {
                setChannel(option)
              }}
            >
              {label(option)}
            </button>
          ))}
        </div>
      </div>
      <InlineError failure={send.state.failure} />
      <textarea
        className="ar-textarea"
        style={{ minHeight: '5rem' }}
        placeholder="Write a reply…"
        aria-label="Reply"
        maxLength={4000}
        value={body}
        onChange={(event) => {
          setBody(event.target.value)
        }}
      />
      <div className="ar-row">
        <Button
          busy={send.state.status === 'running'}
          disabled={!body.trim()}
          onClick={() => {
            void onSend()
          }}
        >
          Send
        </Button>
        <span className="ar-card__spacer" />
        <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
          An unsubscribe link is added to every message, as the law requires.
        </span>
      </div>
    </div>
  )
}

function ChannelChip({ channel }: { channel: string }): JSX.Element {
  return <span className={`app-chan app-chan--${channel}`}>{short(channel)}</span>
}

const LABELS: Record<string, string> = {
  email: 'Email',
  whatsapp: 'WhatsApp',
  sms: 'SMS',
  rcs: 'RCS',
  linkedin: 'LinkedIn',
}

const SHORT: Record<string, string> = {
  email: 'Email',
  whatsapp: 'WA',
  sms: 'SMS',
  rcs: 'RCS',
  linkedin: 'LI',
}

function label(channel: string): string {
  return LABELS[channel] ?? channel
}

function short(channel: string): string {
  return SHORT[channel] ?? channel
}

/**
 * What a delivery state means in a sentence a tenant can act on.
 *
 * **`suppressed` is the one that matters.** AugStash answers an HTTP success and
 * drops the message, so a screen that showed "sent" for it would be reporting a
 * delivery that never happened — the exact failure `DeliveryState` exists to
 * make impossible. Anything unmapped falls through as itself rather than being
 * hidden: a state this table has not caught up with is still true.
 */
const STATES: Record<string, string> = {
  queued: 'queued',
  deferred: 'held by the provider — it will retry',
  sent: 'sent',
  delivered: 'delivered',
  opened: 'opened',
  clicked: 'clicked',
  read: 'read',
  suppressed: 'not delivered — this person opted out',
  failed: 'failed to send',
  undelivered: 'not delivered',
  bounced: 'bounced',
  complained: 'reported as spam',
}

function stateLabel(state: string): string {
  return STATES[state] ?? state
}

/**
 * A timestamp as the list shows it: a time today, a date otherwise.
 *
 * `Asia/Kolkata` explicitly rather than the viewer's zone (CLAUDE.md): every
 * other date in the product renders in IST, and an inbox that disagreed with
 * the audit log about when a reply arrived would be the one screen somebody
 * cannot reconcile.
 */
function when(value: string | null | undefined): string {
  if (!value) return ''
  const at = new Date(value)
  if (Number.isNaN(at.getTime())) return ''
  return at.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}
